// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { Shield, FileText, AlertTriangle, CheckCircle } from "lucide-react";

const DataProtectionTab = () => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold dark:text-slate-100">
        Technische und organisatorische Schutzmaßnahmen
      </h2>

      <div className="grid gap-6">
        {/* Technische Schutzmaßnahmen */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center dark:text-slate-100">
            <Shield className="w-5 h-5 mr-2 primary-color" />
            Technische Schutzmaßnahmen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Verschlüsselung
                </h4>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• AES-256 Verschlüsselung für sensible Daten</li>
                <li>• TLS 1.3 für alle API-Verbindungen</li>
                <li>• bcrypt für Passwort-Hashing (Keycloak)</li>
                <li>• Pseudonymisierung von PII-Daten</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Zugriffskontrolle
                </h4>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• Keycloak Single Sign-On Integration</li>
                <li>• X-API-Key Authentifizierung</li>
                <li>• Rollenbasierte GDPR-Berechtigungen</li>
                <li>• Multi-Faktor-Authentifizierung</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Protokollierung
                </h4>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• Vollständige VHP2 Audit-Trails</li>
                <li>• Hash-Chain für Log-Integrität</li>
                <li>• Anomalie-Erkennung bei Datenzugriffen</li>
                <li>• Automatische Log-Archivierung</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Datensicherung
                </h4>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• Tägliche MariaDB Backups mit Verschlüsselung</li>
                <li>• Georedundante Speicherung</li>
                <li>• Automated Recovery Testing</li>
                <li>• Point-in-Time Recovery (7 Tage)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Organisatorische Schutzmaßnahmen */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center dark:text-slate-100">
            <FileText className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
            Organisatorische Schutzmaßnahmen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Richtlinien & Verfahren
                </h4>
                <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• VHP2 DSGVO Löschkonzept implementiert</li>
                <li>• Stadtplatz.de Incident Response Plan</li>
                <li>• Zweistufiger Löschprozess dokumentiert</li>
                <li>• Data Protection Impact Assessment</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Personalmanagement
                </h4>
                <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• GDPR Officer Schulungen (quartalsweise)</li>
                <li>• Datenschutz-Vertraulichkeitsvereinbarungen</li>
                <li>• Rollenbasierte Zugriffsverwaltung</li>
                <li>• Urban Space GmbH Security Policies</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">
                  Qualitätskontrolle
                </h4>
                <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• Jährliche GDPR-Compliance Audits</li>
                <li>• Penetrationstests (alle 6 Monate)</li>
                <li>• Kontinuierliche Code-Reviews</li>
                <li>• Vulnerability Scanning</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-slate-200">Compliance</h4>
                <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                <li>• DSGVO-Konformität nach VHP2 Standard</li>
                <li>• ISO 27001 Zertifizierung (geplant)</li>
                <li>• BSI IT-Grundschutz Compliance</li>
                <li>• Datenschutz-Folgenabschätzung durchgeführt</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Compliance Status */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center dark:text-slate-100">
            <AlertTriangle className="w-5 h-5 mr-2 text-amber-600 dark:text-amber-400" />
            Compliance-Status
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                <span className="font-medium dark:text-slate-200">
                  DSGVO Art. 32 - Technische Schutzmaßnahmen
                </span>
              </div>
              <span className="text-sm text-green-600 dark:text-green-400">
                Erfüllt
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                <span className="font-medium dark:text-slate-200">
                  DSGVO Art. 17 - Recht auf Löschung
                </span>
              </div>
              <span className="text-sm text-green-600 dark:text-green-400">
                Erfüllt
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                <span className="font-medium dark:text-slate-200">
                  DSGVO Art. 20 - Datenportabilität
                </span>
              </div>
              <span className="text-sm text-green-600 dark:text-green-400">
                Erfüllt
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
                <span className="font-medium dark:text-slate-200">
                  VHP2 Löschkonzept - Implementierung
                </span>
              </div>
              <span className="text-sm text-yellow-600 dark:text-yellow-400">
                95% abgeschlossen
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg dark:border dark:border-slate-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                <span className="font-medium dark:text-slate-200">
                  MariaDB Event Scheduler - Retention
                </span>
              </div>
              <span className="text-sm text-green-600 dark:text-green-400">
                Aktiv
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProtectionTab;
